/**
 * Client Management API
 * Real API integration for http://localhost:3000/api/clients
 */

import axios from "axios";
import { authHeaders } from "./nodeAuthService";
import { API_BASE_URL } from "../config/api";
import { resolveApiUploadUrl } from "../utils/mediaUrl";

const BASE_URL = `${API_BASE_URL}/api/clients`;

// Base URL for serving uploaded images — adjust if your backend differs
export const UPLOADS_BASE_URL = `${API_BASE_URL}/uploads`;

/**
 * Returns a displayable image src from the value stored by the backend.
 * Handles: full URLs, base64 data URIs, bare filenames, and empty values.
 */
export function getClientImageSrc(clientImage) {
  return resolveApiUploadUrl(clientImage);
}

/** Full URL for an uploaded SOW document path from the API */
export function getSowDocumentUrl(sowPath) {
  return resolveApiUploadUrl(sowPath);
}

export async function fetchClients() {
  const { data } = await axios.get(BASE_URL, { headers: authHeaders() });
  // Support both bare array and wrapped { data: [...] } responses
  return Array.isArray(data) ? data : data.data ?? data.clients ?? [];
}

/** Lightweight lookup for dropdowns — accessible to employees (requires assignment view only) */
export async function fetchClientsLookup() {
  const { data } = await axios.get(`${BASE_URL}/lookup`, { headers: authHeaders() });
  return Array.isArray(data) ? data : data.data ?? data.clients ?? [];
}

export async function createClient(payload) {
  let body;
  let headers = { ...authHeaders() };

  if (payload.clientImage instanceof File || payload.sowDocument instanceof File) {
    body = new FormData();
    body.append("clientName", payload.clientName);
    body.append("contactPerson", payload.contactPerson);
    body.append("clientEmail", payload.clientEmail);
    body.append("clientMobile", payload.clientMobile);
    if (payload.clientImage instanceof File) {
      body.append("clientImage", payload.clientImage);
    }
    if (payload.sowDocument instanceof File) {
      body.append("sowDocument", payload.sowDocument);
    }
    // axios sets Content-Type: multipart/form-data automatically for FormData
  } else {
    body = {
      clientName: payload.clientName,
      contactPerson: payload.contactPerson,
      clientEmail: payload.clientEmail,
      clientMobile: payload.clientMobile,
      clientImage: payload.clientImage || "",
      sowDocument: payload.sowDocument || "",
    };
    headers["Content-Type"] = "application/json";
  }

  const { data } = await axios.post(BASE_URL, body, { headers });
  return data.data ?? data;
}

export async function updateClient(id, payload) {
  let body;
  let headers = { ...authHeaders() };

  if (payload.clientImage instanceof File || payload.sowDocument instanceof File) {
    body = new FormData();
    body.append("clientName", payload.clientName);
    body.append("contactPerson", payload.contactPerson);
    body.append("clientEmail", payload.clientEmail);
    body.append("clientMobile", payload.clientMobile);
    if (payload.clientImage instanceof File) {
      body.append("clientImage", payload.clientImage);
    }
    if (payload.sowDocument instanceof File) {
      body.append("sowDocument", payload.sowDocument);
    }
  } else {
    body = {
      clientName: payload.clientName,
      contactPerson: payload.contactPerson,
      clientEmail: payload.clientEmail,
      clientMobile: payload.clientMobile,
    };
    // Only include clientImage when explicitly provided (avoids overwriting with undefined)
    if (payload.clientImage !== undefined) {
      body.clientImage = payload.clientImage;
    }
    if (payload.sowDocument !== undefined) {
      body.sowDocument = payload.sowDocument;
    }
    headers["Content-Type"] = "application/json";
  }

  const { data } = await axios.put(`${BASE_URL}/${id}`, body, { headers });
  return data.data ?? data;
}

/** Clients created by (assigned to) the logged-in AM or HoD, with active project counts */
export async function fetchMyAssignedClients() {
  const { data } = await axios.get(`${BASE_URL}/my-clients`, { headers: authHeaders() });
  return Array.isArray(data) ? data : data.data ?? [];
}

export async function deleteClient(id) {
  const { data } = await axios.delete(`${BASE_URL}/${id}`, {
    headers: authHeaders(),
  });
  return data;
}